/**
 * AssumptionsApp — the Assumptions Hub feature, embedded in the portal.
 *
 * Orchestrates the manage/view tabs and wires the components to the
 * useAssumptions hook. The standalone Hub's MainLayout (its own sidebar) is
 * gone — in the portal this renders inside the existing shell via the /data
 * route, so the manage/view switch is an in-page tab bar instead.
 *
 * Ported to TSX + Nordic; framer-motion removed (CSS via `view-in`).
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Trash2 } from "lucide-react";

import AssumptionGrid from "./AssumptionGrid";
import AssumptionList from "./AssumptionList";
import ConfirmModal, { type ConfirmModalVariant } from "./ConfirmModal";
import FileUploader from "./FileUploader";
import useAssumptions from "./useAssumptions";
import type { GridRow, TemplateConfig } from "./types";

type Tab = "manage" | "view";

interface ModalState {
  open: boolean;
  title: string;
  message: string;
  variant: ConfirmModalVariant;
  onConfirm: (() => void) | null;
}

export default function AssumptionsApp() {
  const [activeTab, setActiveTab] = useState<Tab>("manage");
  const [data, setData] = useState<GridRow[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [fileId, setFileId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [templateConfig, setTemplateConfig] = useState<TemplateConfig | null>(
    null,
  );
  const [modal, setModal] = useState<ModalState>({
    open: false,
    title: "",
    message: "",
    variant: "alert",
    onConfirm: null,
  });

  const {
    files: assumptionFiles,
    loading: isAssumptionsLoading,
    loadFiles: loadAssumptionFiles,
    fetchContent,
    upload,
    remove: deleteFile,
    templates,
    fileTemplateMappings,
    loadTemplates,
    fetchTemplate,
    loadFileTemplateMappings,
  } = useAssumptions();

  useEffect(() => {
    if (activeTab === "view") {
      loadAssumptionFiles();
      loadTemplates();
      loadFileTemplateMappings();
    }
  }, [
    activeTab,
    loadAssumptionFiles,
    loadTemplates,
    loadFileTemplateMappings,
  ]);

  const handleDataLoaded = (
    loadedData: GridRow[],
    name: string,
    templateCfg: TemplateConfig | null = null,
  ) => {
    setData(loadedData);
    setFileName(name);
    setFileId(null);
    setTemplateConfig(templateCfg);
  };

  const handleCloseEditor = () => {
    setData(null);
    setFileName("");
    setFileId(null);
    setTemplateConfig(null);
  };

  const handleDelete = () => {
    const idToDelete = fileId || fileName;
    setModal({
      open: true,
      title: "Delete Dataset",
      message: `Are you sure you want to PERMANENTLY DELETE "${fileName}" from the server? This action cannot be undone.`,
      variant: "danger",
      onConfirm: async () => {
        setModal((m) => ({ ...m, open: false }));
        setIsDeleting(true);
        try {
          await deleteFile(idToDelete);
          handleCloseEditor();
          if (activeTab === "view") loadAssumptionFiles();
          setModal({
            open: true,
            title: "Deleted",
            message: "File deleted successfully.",
            variant: "success",
            onConfirm: () => setModal((m) => ({ ...m, open: false })),
          });
        } catch (err) {
          setModal({
            open: true,
            title: "Delete Failed",
            message:
              "Failed to delete file: " +
              (err instanceof Error ? err.message : String(err)),
            variant: "alert",
            onConfirm: () => setModal((m) => ({ ...m, open: false })),
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const handleSave = async (
    updatedData: GridRow[],
    newFileName: string | null = null,
  ) => {
    const rawName = newFileName || fileId || fileName;
    const saveName = rawName.includes("/")
      ? rawName.split("/").pop() || rawName
      : rawName;
    const templateId = templateConfig?.template_id || "";
    const templateVersion = "1.0";
    const originalS3Key = newFileName ? "" : (fileId ?? "");
    await upload(updatedData, saveName, templateId, templateVersion, originalS3Key);
    if (newFileName) setFileName(newFileName);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleSelectFile = async (fileItem: {
    id: string;
    name: string;
  }) => {
    setIsLoadingFile(true);
    setActiveTab("manage");
    setFileName(fileItem.name || "Loading...");
    setFileId(fileItem.id);
    setData(null);

    try {
      const fileContent = await fetchContent(fileItem.id);
      if (!fileContent || !fileContent.data) {
        setActiveTab("view");
        setData(null);
        return;
      }

      // Mappings are keyed by full S3 key (backend convention). fileItem.id
      // IS the s3Key (see useAssumptions.loadFiles). Fall back to a
      // filename-suffix match for robustness against any key drift.
      const mappings = fileTemplateMappings?.mappings || {};
      let mapping = mappings[fileItem.id];
      if (!mapping) {
        const bySuffix = Object.entries(mappings).find(([k]) =>
          k.endsWith("/" + fileItem.name) || k === fileItem.name,
        );
        if (bySuffix) mapping = bySuffix[1];
      }
      let resolvedTemplateConfig: TemplateConfig | null = null;

      if (mapping && mapping.template_id) {
        const templateFile = (templates || []).find(
          (t) => t.name.replace(".json", "") === mapping.template_id,
        );
        if (templateFile) {
          try {
            const templateData = await fetchTemplate(templateFile.id);
            if (templateData) {
              resolvedTemplateConfig = {
                template_name: templateData.template_name,
                template_id: mapping.template_id,
                s3_key: templateFile.id,
                columns: templateData.columns,
              };
            }
          } catch (err) {
            console.error("Failed to load template for editing:", err);
          }
        }
      }

      setData(fileContent.data);
      setFileName(fileContent.fileName);
      setFileId(
        fileContent.s3_key ||
          fileContent.s3Key ||
          fileContent.id ||
          fileItem.id,
      );
      setTemplateConfig(resolvedTemplateConfig);
    } finally {
      setIsLoadingFile(false);
    }
  };

  const tabBtn = (tab: Tab, label: string) => {
    const active = activeTab === tab;
    return (
      <button
        onClick={() => setActiveTab(tab)}
        className="px-4 py-2 text-[11px] font-mono uppercase tracking-[0.14em] rounded t-base focus-ring"
        style={{
          background: active ? "var(--accent-soft)" : "transparent",
          color: active ? "var(--accent)" : "var(--ink-3)",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="p-8 view-in h-full overflow-y-auto">
      <header className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div className="flex flex-col gap-1">
          <h1
            className="font-serif text-[28px] leading-tight"
            style={{ color: "var(--ink)" }}
          >
            {activeTab === "manage" ? "Manage Assumptions" : "Existing Assumptions"}
          </h1>
          <p
            className="text-[14.5px] font-serif italic"
            style={{ color: "var(--ink-3)" }}
          >
            {activeTab === "manage"
              ? data
                ? "Edit your assumption dataset below."
                : "Create assumption datasets."
              : "Select a dataset to view or edit."}
          </p>
        </div>
        {activeTab === "manage" &&
          data &&
          fileName !== "New Assumptions" && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-4 py-2 text-[11px] font-mono uppercase tracking-[0.12em] rounded border flex items-center gap-2 t-base focus-ring"
              style={{
                color: "var(--neg)",
                borderColor: "var(--neg)",
                background: "var(--neg-soft, rgba(156,90,82,0.08))",
              }}
            >
              <Trash2 size={14} strokeWidth={1.5} />
              {isDeleting ? "Deleting..." : "Delete Dataset"}
            </button>
          )}
      </header>

      {/* Tab bar */}
      <div
        className="inline-flex gap-1 p-1 rounded-lg border hairline mb-6"
        style={{ background: "var(--bg-elev)" }}
      >
        {tabBtn("manage", "Manage")}
        {tabBtn("view", "View")}
      </div>

      <section className="min-h-[400px]">
        {activeTab === "manage" && (
          <div className="view-in">
            {isLoadingFile ? (
              <div className="py-20 flex flex-col items-center justify-center gap-4">
                <div
                  className="w-10 h-10 rounded-full animate-spin"
                  style={{
                    border: "3px solid var(--accent-soft)",
                    borderTopColor: "var(--accent)",
                  }}
                />
                <p
                  className="text-[13px] font-serif"
                  style={{ color: "var(--ink-3)" }}
                >
                  Loading {fileName}...
                </p>
              </div>
            ) : !data ? (
              <div className="py-12">
                <FileUploader onDataLoaded={handleDataLoaded} />
              </div>
            ) : (
              <AssumptionGrid
                data={data}
                fileName={fileName}
                onSave={handleSave}
                onClose={handleCloseEditor}
                templateConfig={templateConfig}
              />
            )}
          </div>
        )}

        {activeTab === "view" && (
          <div className="view-in">
            {isAssumptionsLoading ? (
              <p className="font-serif text-[14px]" style={{ color: "var(--ink-3)" }}>
                Loading...
              </p>
            ) : (
              <AssumptionList
                items={assumptionFiles}
                onSelect={handleSelectFile}
                templates={templates}
                fileTemplateMappings={fileTemplateMappings}
              />
            )}
          </div>
        )}
      </section>

      {/* Success toast */}
      {showSuccess && (
        <div
          className="fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-lg flex items-center gap-3 z-50 pointer-events-none view-in"
          style={{ background: "var(--ink)", color: "var(--bg)" }}
        >
          <CheckCircle2 size={18} strokeWidth={1.5} style={{ color: "var(--pos)" }} />
          <span className="font-mono text-[12px] uppercase tracking-[0.1em]">
            Data saved successfully
          </span>
        </div>
      )}

      <ConfirmModal
        isOpen={modal.open}
        title={modal.title}
        message={modal.message}
        variant={modal.variant}
        onConfirm={modal.onConfirm || (() => setModal((m) => ({ ...m, open: false })))}
        onCancel={() => setModal((m) => ({ ...m, open: false }))}
      />
    </div>
  );
}
